/**
 * LeadSources — Hot Leads Webhook source management (P2 CRM 2.0).
 *
 * Admin can create signed webhook endpoints for Website / LinkedIn /
 * Facebook / Instagram / Generic intake. Each source has a unique secret
 * shown ONCE on creation (or on rotation). Provides ready-to-paste
 * integration snippets per source kind.
 */
import React, { useEffect, useState } from 'react';
import {
  Webhook, Plus, RefreshCw, Power, PowerOff, Trash2, Copy, Loader2,
  Globe, Linkedin, Facebook, Instagram, Code2, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  website:   { Icon: Globe,     color: 'bg-slate-100 text-slate-700' },
  linkedin:  { Icon: Linkedin,  color: 'bg-blue-100 text-blue-800' },
  facebook:  { Icon: Facebook,  color: 'bg-indigo-100 text-indigo-800' },
  instagram: { Icon: Instagram, color: 'bg-pink-100 text-pink-800' },
  generic:   { Icon: Code2,     color: 'bg-amber-100 text-amber-800' },
};

const sampleBody = (kind) => {
  if (kind === 'linkedin') return JSON.stringify({
    firstName: 'John', lastName: 'Smith',
    emailAddress: 'john.smith@example.com',
    phoneNumber: '+971501234567',
    companyName: 'Acme Trading FZCO', jobTitle: 'CEO',
    country: 'United Arab Emirates', industry: 'trading',
  }, null, 2);
  if (kind === 'facebook' || kind === 'instagram') return JSON.stringify({
    form_id: 'YOUR_FORM_ID', ad_id: 'YOUR_AD_ID',
    field_data: [
      { name: 'full_name', values: ['Maria Garcia'] },
      { name: 'email', values: ['maria@example.com'] },
      { name: 'phone_number', values: ['+971502223344'] },
      { name: 'company_name', values: ['Garcia Holdings LLC'] },
    ],
  }, null, 2);
  if (kind === 'generic') return JSON.stringify({
    company_name: 'Brand X LLC', contact_person: 'Sara Brand',
    email: 'sara@brandx.com', phone: '+971500000000',
    role: 'client', country: 'United Arab Emirates',
    estimated_value: 250000, currency: 'AED',
  }, null, 2);
  // website default
  return JSON.stringify({
    name: 'Sara Smith', email: 'sara@example.com',
    phone: '+971501234567', company: 'Smith & Co LLC',
    country: 'United Arab Emirates', message: 'Interested in your services',
  }, null, 2);
};

const buildCurlSnippet = (sourceId, secret, kind) => {
  const body = sampleBody(kind);
  const url = `${API_URL}/api/public/leads/intake/${sourceId}`;
  return `# Run from any server (Linux/Mac):
TS=$(date -u +"%Y-%m-%dT%H:%M:%SZ")
BODY='${body.replace(/\n/g, '\\\n')}'
SECRET='${secret || 'YOUR_SECRET'}'
SIG=$(echo -n "$BODY" | openssl dgst -sha256 -hmac "$SECRET" | sed 's/^.* //')
curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "X-BMP-Signature: sha256=$SIG" \\
  -H "X-BMP-Timestamp: $TS" \\
  -d "$BODY"`;
};

const LeadSources = () => {
  const { toast } = useToast();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('website');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null); // {id, secret} after create/rotate
  const [snippet, setSnippet] = useState(null);

  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/lead-sources`, auth);
      const data = await r.json();
      setSources(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createSource = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/lead-sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), kind }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      const j = await r.json();
      setRevealed({ id: j.id, secret: j.secret, kind: j.kind });
      setSnippet(buildCurlSnippet(j.id, j.secret, j.kind));
      setName(''); setCreateOpen(false);
      await load();
      toast({ title: 'Source created' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const rotate = async (id, k) => {
    if (!window.confirm('Rotate the secret? The current one will stop working immediately.')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/lead-sources/${id}/rotate-secret`,
        { method: 'POST', ...auth });
      if (!r.ok) throw new Error('Failed');
      const j = await r.json();
      setRevealed({ id, secret: j.secret, kind: k });
      setSnippet(buildCurlSnippet(id, j.secret, k));
      toast({ title: 'Secret rotated' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const toggle = async (id, currentlyActive) => {
    try {
      await fetch(`${API_URL}/api/admin/lead-sources/${id}/active`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: !currentlyActive }),
      });
      await load();
      toast({ title: currentlyActive ? 'Disabled' : 'Activated' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this source? Existing webhook requests will be rejected.')) return;
    try {
      await fetch(`${API_URL}/api/admin/lead-sources/${id}`, { method: 'DELETE', ...auth });
      await load();
      toast({ title: 'Source deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard' });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="lead-sources-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif text-[#0F172A] flex items-center gap-2">
            <Webhook className="w-7 h-7 text-[#C9A86A]" />
            Hot Leads Webhook
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Public signed endpoints for Website + Social Media lead capture.
            Each lead triggers the full P5/P6/P7/P8 workflow chain automatically.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}
          className="bg-[#0F172A] hover:bg-[#1E293B]"
          data-testid="lead-sources-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Source
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
        </CardContent></Card>
      ) : sources.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 font-medium">No sources yet</div>
          <div className="text-sm text-slate-500 mt-1">
            Create one for your website, LinkedIn Lead Gen Forms, Meta Lead Ads or Zapier.
          </div>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="lead-sources-list">
          {sources.map((s) => {
            const M = KIND_META[s.kind] || KIND_META.generic;
            return (
              <Card key={s.id} data-testid={`source-row-${s.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${M.color}`}>
                        <M.Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0F172A]">{s.name}</div>
                        <div className="text-xs text-slate-500 font-mono">
                          /api/public/leads/intake/{s.id}
                        </div>
                      </div>
                      <Badge className={`${M.color} text-[10px] uppercase`}>{s.kind}</Badge>
                      {s.active
                        ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Active</Badge>
                        : <Badge className="bg-slate-200 text-slate-600 text-[10px]">Disabled</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>Leads: <b className="text-[#0F172A]">{s.leads_count || 0}</b></span>
                      {s.last_intake_at && (
                        <span>Last: {(s.last_intake_at || '').slice(0, 19).replace('T', ' ')}</span>
                      )}
                      <span className="font-mono text-slate-400">{s.secret_preview}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => {
                        setSnippet(buildCurlSnippet(s.id, '', s.kind));
                      }} data-testid={`snippet-${s.id}`}>
                        <Code2 className="w-3.5 h-3.5 mr-1" /> Snippet
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rotate(s.id, s.kind)}
                        title="Rotate secret"
                        data-testid={`rotate-${s.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(s.id, s.active)}
                        title={s.active ? 'Disable' : 'Enable'}
                        data-testid={`toggle-${s.id}`}>
                        {s.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(s.id)}
                        title="Delete"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid={`delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="lead-source-create-dialog">
          <DialogHeader>
            <DialogTitle>New webhook source</DialogTitle>
            <DialogDescription className="sr-only">
              Create a signed intake endpoint. The secret is shown once on creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600">Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. grupobmp.com contact form"
                data-testid="lead-source-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600">Kind *</label>
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="mt-1 w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                data-testid="lead-source-kind-select">
                <option value="website">Website</option>
                <option value="linkedin">LinkedIn Lead Gen Forms</option>
                <option value="facebook">Facebook Lead Ads</option>
                <option value="instagram">Instagram Lead Ads</option>
                <option value="generic">Generic / Zapier / Custom</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createSource} disabled={busy || !name.trim()}
              className="bg-[#0F172A] hover:bg-[#1E293B]"
              data-testid="lead-source-create-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal secret dialog */}
      {revealed && (
        <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
          <DialogContent className="max-w-2xl" data-testid="lead-source-secret-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" /> Secret created
              </DialogTitle>
              <DialogDescription className="sr-only">
                Copy and store the secret now. It will not be shown again.
              </DialogDescription>
            </DialogHeader>
            <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <span>This is the only time the full secret will be displayed.
              Copy and store it securely.</span>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600">Secret</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 rounded bg-slate-900 text-emerald-300 text-xs font-mono break-all"
                  data-testid="lead-source-revealed-secret">
                  {revealed.secret}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(revealed.secret)}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600">cURL example</label>
              <div className="flex items-start gap-2">
                <pre className="flex-1 p-3 rounded bg-slate-900 text-slate-100 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap"
                  data-testid="lead-source-revealed-snippet">{snippet}</pre>
                <Button size="sm" variant="outline" onClick={() => copy(snippet)}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Snippet-only dialog (after creation) */}
      {snippet && !revealed && (
        <Dialog open={!!snippet} onOpenChange={() => setSnippet(null)}>
          <DialogContent className="max-w-2xl" data-testid="lead-source-snippet-dialog">
            <DialogHeader>
              <DialogTitle>Integration snippet</DialogTitle>
              <DialogDescription className="sr-only">
                Replace YOUR_SECRET with the secret you stored on creation/rotation.
              </DialogDescription>
            </DialogHeader>
            <pre className="p-3 rounded bg-slate-900 text-slate-100 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
              {snippet}
            </pre>
            <DialogFooter>
              <Button onClick={() => copy(snippet)}>
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LeadSources;
